import React, { useState, useEffect, useMemo, memo } from "react";
import "./ListsStyles.css";
import moment from "moment";
import useFavorites from "../../../utils/useFavorites";
import { imageLoader } from "../../../utils/imageLoader";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSingleLot,
  fetchLots,
} from "../../../redux/actions/immobilierActions";
import EyeIconLot from "../../../components/commons/EyeIconLot";
import { openModal } from "../../../redux/actions/modalActions";
import MemoizedPdfThumbnail from "./MemoizedPdfThumbnail";

const ListeLots = ({
  fav = true,
  lots,
  filterInput,
  setDisplayedLot,
  openNewtab = true,
  ongletType,
  ongletId,
  query,
  sortConfig,
  setSortConfig,
}) => {
  const { handleAddToFavorites, isLotInFavorites } = useFavorites();
  const dispatch = useDispatch();
  const isDisplayed = useSelector(
    (state) => state.displayHonorairesReducer.isDisplayed
  );
  // Add state for tracking window size
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isWideScreen = windowWidth > 1450;
  const showImages = windowWidth >= 1024;
  const isSmallScreen = windowWidth < 1200;
  // Add effect to update window size on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  // Helper function to find PDF document with priority
  const findPdfDocument = (lot) => {
    return lot?.documents_pdfs?.find(
      (doc) =>
        doc?.type === "plan_vente" &&
        doc?.document?.[0]?.url?.toLowerCase().includes(".pdf")
    ) ||
    lot?.documents_pdfs?.find(
      (doc) =>
        doc?.type === "plan_etage" &&
        doc?.document?.[0]?.url?.toLowerCase().includes(".pdf")
    );
  };

  // Memoize PDF documents for each lot to avoid recalculation
  const lotPdfDocuments = useMemo(() => {
    if (!lots) return {};
    
    const documents = {};
    lots.forEach(lot => {
      if (lot?.documentId) {
        documents[lot.documentId] = findPdfDocument(lot);
      }
    });
    
    return documents;
  }, [lots]);

  const getBadgeColor = (index) => {
    const colors = [
      "bg-green",
      "bg-blue",
      "bg-purple",
      "bg-orange",
      "bg-pink",
      "bg-yellow",
      "bg-red",
    ];
    return colors[index % colors.length];
  };
  const handleLotClick = async (documentId) => {
    dispatch(fetchSingleLot(documentId));
  };
  const changeDisplayedLot = (lot) => {
    setDisplayedLot(lot);
    const targetElement = document.getElementById("lotsDetails");
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle lot image click
  const handleLotImageClick = (e, lot) => {
    e.stopPropagation();
    e.preventDefault();

    // Use memoized PDF document
    const pdfDocument = lotPdfDocuments[lot.documentId];

    const imageUrl =
      pdfDocument?.document?.[0]?.url || lot?.images?.[0]?.image?.[0]?.url;

    if (imageUrl) {
      const isPDF = !!pdfDocument;
      const title = `${lot.reference} - ${isPDF ? "Document PDF" : "Image du lot"
        }`;

      const content = isPDF ? (
        <iframe
          src={imageLoader(imageUrl)}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: "8px",
          }}
          title="PDF Viewer"
        />
      ) : (
        <img
          src={imageLoader(imageUrl)}
          alt={`Image du lot ${lot.reference}`}
          style={{
            width: "100%",
            height: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      );

      dispatch(openModal({ title, content }));
    }
  };

  // Handle programme image click
  const handleProgrammeImageClick = (e, lot) => {
    e.stopPropagation();
    const imageUrl =
      lot?.images?.[1]?.image?.[0]?.url || lot?.images?.[0]?.image?.[0]?.url;

    if (imageUrl) {
      const title = `${lot.programme_parent?.name} - Image du programme`;

      const content = (
        <img
          src={imageLoader(imageUrl)}
          alt={`Image du programme ${lot.programme_parent?.name}`}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      );

      dispatch(openModal({ title, content }));
    }
  };

  // Function to handle sort request
  const requestSort = (key) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    // Map UI keys to API field names
    let sortField = key;

    // Dispatch action to fetch sorted lots with the direction parameter
    dispatch(
      fetchLots(
        {
          ...query,
          search: filterInput,
          page: 1,
          sort: sortField,
          direction,
        },
        ongletType,
        ongletId
      )
    );
  };

  // Helper function to render sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig && sortConfig.key !== key) {
      return <img src="/icons/sorts.svg" alt="sort" className="sort-icon" />;
    }
    return sortConfig && sortConfig.direction === "asc" ? (
      <img
        src="/icons/sort_up.svg"
        alt="sort ascending"
        className="sort-icon"
      />
    ) : (
      <img
        src="/icons/sort_down.svg"
        alt="sort descending"
        className="sort-icon"
      />
    );
  };

  return (
    <div className="">
      <div className="table mt-4">
        {/* Header */}
        <div className="table-lot-header pink text-center ">
          <div
            className="table-cell sortableHeaderStyle"
            onClick={() => requestSort("reference")}
            title="Référence du lot"
          >
            <img src="./icons/lots.svg" width={35} height={35} alt="lot" /> {getSortIndicator("reference")}
          </div>
          <div
            className="table-cell sortableHeaderStyle"
            title="Nom du programme"
            onClick={() => requestSort("programme_parent.name")}
          >
            <img src="./icons/programmes.svg" width={40} height={40} alt="programme" /> {getSortIndicator("programme_parent.name")}
          </div>
          {isWideScreen && (
            <div className="table-cell" title="Dispositif applicable">
              <img src="./icons/project-types.svg" width={40} height={40} alt="dispositif" /> {getSortIndicator("dispositif")}
            </div>
          )}
          {!isSmallScreen && (
            <div
              className="table-cell sortableHeaderStyle"
              onClick={() => requestSort("typology")}
              title="Type de bien"
            >
              <img src="./icons/typologie.svg" width={40} height={40} alt="typologie" /> {getSortIndicator("typology")}
            </div>
          )}
          {!isSmallScreen ? (
            <div
              className="table-cell sortableHeaderStyle"
              onClick={() => requestSort("surface")}
              title="Surface intérieure"
            >
              <img src="./icons/surface-int.svg" width={40} height={40} alt="surface int" /> {getSortIndicator("surface")}
            </div>
          ) : (
            <div
              className="table-cell sortableHeaderStyle"
              onClick={() => requestSort("surface")}
              title="Surface intérieure / Surface extérieure"
            >
              <img src="./icons/surface-int.svg" width={40} height={40} alt="surface int/ext" /> {getSortIndicator("surface")}
            </div>
          )}
          {!isSmallScreen && (
            <div
              className="table-cell sortableHeaderStyle"
              onClick={() => requestSort("surface_exterieur")}
              title="Surface extérieure"
            >
              <img src="./icons/surface-ext.svg" width={40} height={40} alt="surface ext" /> {getSortIndicator("surface_exterieur")}
            </div>
          )}
          {isWideScreen && (
            <div
              className="table-cell sortableHeaderStyle"
              onClick={() => requestSort("floor")}
              title="Étage du bien"
            >
              <img src="./icons/etage.svg" width={40} height={40} alt="etage" /> {getSortIndicator("floor")}
            </div>
          )}
          <div
            className="table-cell sortableHeaderStyle"
            onClick={() => requestSort("geo_orientations")}
            title="Exposition"
          >
            <img src="./icons/exposition.svg" width={40} height={40} alt="exposition" /> {getSortIndicator("geo_orientations")}
          </div>
          {isWideScreen && (
            <div
              className="table-cell sortableHeaderStyle"
              title="Nombre de places de parking"
              onClick={() => requestSort("parking_count")}
            >
              <img src="./icons/parking.svg" width={40} height={40} alt="parking" /> {getSortIndicator("parking_count")}
            </div>
          )}
          <div
            className="table-cell sortableHeaderStyle"
            onClick={() => requestSort("price_total")}
            title="Prix total du bien"
          >
            <img src="./icons/prix.svg" width={40} height={40} alt="prix" /> {getSortIndicator("price_total")}
          </div>
          {isWideScreen && (
            <div
              className="table-cell sortableHeaderStyle"
              title="Montant des honoraires"
              onClick={() => requestSort("commision_value")}
            >
              <img src="./icons/honoraires.svg" width={40} height={40} alt="honoraires" /> {getSortIndicator("commision_value")}
            </div>
          )}
          <div className="table-cell" title="Ajouter aux favoris">
            <img src="./icons/favoris.svg" width={40} height={40} alt="favoris" />
          </div>
        </div>

        {/* Rows */}
        {lots?.length > 0 ? (
          lots.map((lot) => (
            <div
              key={lot.documentId}
              className="table-lot-row cursor-pointer hoverborder position-relative"
              /* onClick={() => handleLotClick(lot?.documentId)} */
              onClick={() => {
                openNewtab
                  ? handleLotClick(lot?.documentId)
                  : changeDisplayedLot(lot);
              }}
            >
              {/* Lot */}
              <div className="table-cell p-0">
                <div className="d-flex align-items-center justify-content-center gap-2">
                  {showImages && (
                    <div className="plan-image position-relative overflow-hidden">
                      <span
                        className={
                          lot?.disponibility?.disponibilite === "Disponible"
                            ? "position-absolute top-0 start-0 bg-green py-1 px-2 radius5 fs11 "
                            : lot?.disponibility?.disponibilite === "Optionné"
                              ? "position-absolute top-0 start-0 bg-yellow py-1 px-2 radius5 fs11 "
                              : "position-absolute top-0 start-0 bg-red py-1 px-2 radius5 fs11 "
                        }
                      >
                        {lot?.disponibility?.disponibilite === "Disponible"
                          ? "Disponible"
                          : lot?.disponibility?.disponibilite === "Optionné"
                            ? "Optionné"
                            : "Réservé"}
                      </span>
                      {(() => {
                        // Use memoized PDF document
                        const pdfDocument = lotPdfDocuments[lot.documentId];

                        return pdfDocument?.document?.length > 0 ? (
                          <div onClick={(e) => handleLotImageClick(e, lot)}>
                            <MemoizedPdfThumbnail
                              url={imageLoader(pdfDocument.document[0].url)}
                            />
                          </div>
                        ) : null;
                      })()} 
                      {(() => {
                        // Use memoized PDF document
                        const pdfDocument = lotPdfDocuments[lot.documentId];

                        // Only show images/logo if no PDF was found
                        if (pdfDocument?.document?.length > 0) return null;

                        // Show first image if available, otherwise show promoteur logo
                        return lot?.images?.length > 0 &&
                          lot?.images[0]?.image?.length > 0 ? (
                          <img
                            src={imageLoader(lot?.images[0]?.image[0]?.url)}
                            alt=""
                            className="radius5 plan-image"
                            onClick={(e) => handleLotImageClick(e, lot)}
                          />
                        ) : (
                          <img
                            src={imageLoader(
                              lot.programme_parent?.promoteur?.logo?.url
                            )}
                            alt=""
                            className="radius5 plan-image"
                            onClick={(e) => handleLotImageClick(e, lot)}
                          />
                        );
                      })()}
                    </div>
                  )}
                  <p
                    className="mb-0 fs16 darkGrey semibold text-truncate"
                    title={lot.reference}
                    style={
                      isWideScreen ? { maxWidth: "20%" } : { maxWidth: "100%" }
                    }
                  >
                    {lot.reference}
                  </p>
                </div>
              </div>

              {/* Programme */}
              <div className="table-cell p-0">
                <div className={`d-flex align-items-center gap-2`}>
                  {showImages && (
                    <div className="program-image">
                      {lot?.images?.[1]?.image?.[0]?.url ? (
                        <img
                          src={imageLoader(lot.images[1].image[0].url)}
                          alt=""
                          onClick={(e) => handleProgrammeImageClick(e, lot)}
                        />
                      ) : lot?.images?.[0]?.image?.[0]?.url ? (
                        <img
                          src={imageLoader(lot.images[0].image[0].url)}
                          alt=""
                          onClick={(e) => handleProgrammeImageClick(e, lot)}
                        />
                      ) : (
                        <img
                          src={imageLoader(
                            lot.programme_parent?.promoteur?.logo?.url
                          )}
                          alt=""
                          className="radius5"
                          onClick={(e) => handleProgrammeImageClick(e, lot)}
                        />
                      )}
                    </div>
                  )}

                  <div className={"d-flex flex-column text-start text-wrap"}>
                    <div>
                      <p
                        className="mb-0 fs16 darkGrey semibold text-wrap"
                        title={lot.programme_parent?.name}
                      >
                        {lot.programme_parent?.name}
                      </p>
                      <p className="text-muted mb-0 fs11 darkGrey regular">
                        {lot.programme_parent?.address}
                      </p>
                    </div>
                    <div>
                      <p className="mb-0 fs11 darkGrey semibold">
                        {lot.programme_parent?.municipality}
                      </p>
                      <p className="text-muted mb-0 fs11 darkGrey regular">
                        {lot.programme_parent?.delivery_date
                          ? moment(lot.programme_parent?.delivery_date).format(
                            "YYYY"
                          ) +
                          " T-" +
                          moment(
                            lot.programme_parent?.delivery_date
                          ).quarter()
                          : "NC"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dispositif */}
              {isWideScreen && (
                <div className="table-cell p-0">
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex flex-column justify-content-between gap6">
                      {lot.project_types?.length > 0 ? (
                        lot.project_types.map((badge) => (
                          <span
                            key={badge.documentId}
                            className={`fs11 ${getBadgeColor(
                              lot.project_types.indexOf(badge)
                            )} radius5 py-1 px-2 text-white badge-ellipsis`}
                            title={badge.label}
                          >
                            {badge.label}
                          </span>
                        ))
                      ) : (
                        <span className="fs11 bg-green radius5 py-1 px-2 text-white badge-ellipsis">
                          NC
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Type */}
              {!isSmallScreen && (
                <div className="table-cell text-capitalize">
                  <span
                    className="d-block text-truncate"
                    title={
                      lot.typology && lot.typology !== "unknown"
                        ? lot.typology
                        : "NC"
                    }
                  >
                    {lot.typology && lot.typology !== "unknown"
                      ? lot.typology
                      : "NC"}
                  </span>
                </div>
              )}

              {/* Surfaces */}
              {!isSmallScreen ? (
                <>
                  <div className="table-cell">
                    <span
                      className="d-block text-truncate"
                      title={`Surface intérieure: ${lot.surface || "NC"} m²`}
                    >
                      {lot.surface || "NC"}
                    </span>
                  </div>
                  <div className="table-cell">
                    <span
                      className="d-block text-truncate"
                      title={`Surface extérieure: ${lot.surface_exterieur || "NC"
                        } m²`}
                    >
                      {lot.surface_exterieur || "NC"}
                    </span>
                  </div>
                </>
              ) : (
                <div className="table-cell">
                  <span
                    className="d-block text-truncate"
                    title={`Surface intérieure: ${lot.surface || "NC"
                      } m² / Surface extérieure: ${lot.surface_exterieur
                        ? lot.surface_exterieur?.toFixed(2) || 0
                        : "NC"
                      } m²`}
                  >
                    {lot.surface || "NC"} /{" "}
                    {lot.surface_exterieur
                      ? lot.surface_exterieur?.toFixed(2) || 0
                      : "NC"}
                  </span>
                </div>
              )}

              {/* Étage */}
              {isWideScreen && (
                <div className="table-cell">
                  {lot.floor === null
                    ? "NC"
                    : lot.floor === 0
                      ? "RDC"
                      : lot.floor === 1
                        ? "1er"
                        : lot.floor + "ème"}
                </div>
              )}

              {/* Orientation */}
              <div className="table-cell">
                <span
                  className="d-block text-truncate"
                  title={
                    lot?.geo_orientations?.length > 0
                      ? lot?.geo_orientations
                        .map((orientation) => orientation.orientation)
                        .join(", ")
                      : "N/C"
                  }
                >
                  {lot?.geo_orientations?.length > 0
                    ? lot?.geo_orientations
                      .map((orientation) => orientation.orientation)
                      .join(", ")
                    : "N/C"}
                </span>
              </div>

              {/* Parking */}
              {isWideScreen && (
                <div className="table-cell">{lot.parking_count || "0"}</div>
              )}

              {/* Prix */}
              <div className="table-cell">
                <div className="text-center">
                  <small className="text-muted fs11 regular darkGrey">
                    {"TVA " +
                      (lot.price_vat_rate ? lot.price_vat_rate + "%" : "NC") +
                      ""}
                  </small>
                  <p className="mb-0 fs16 semibold darkGrey">
                    {Intl.NumberFormat("fr-FR").format(lot.price_total) + " €"}
                  </p>
                </div>
              </div>

              {/* Honoraires */}
              {isWideScreen && (
                <div className="table-cell">
                  {isDisplayed
                    ? lot.commission
                      ? lot.commission.match(/\d+(\.\d+)?/)[0] + "%"
                      : "NC"
                    : "***"}
                </div>
              )}

              {/* Action */}
              <div className="table-cell">
                <img
                  src={
                    isLotInFavorites(lot.documentId)
                      ? "./icons/favorite-filled.svg"
                      : "./icons/favorite-outline.svg"
                  }
                  alt="favorite"
                  height={35}
                  width={35}
                  className="cursor-pointer p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToFavorites(lot.documentId, "Lot");
                  }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  right: "-20px",
                  top: "-10px",
                }}
              >
                <EyeIconLot lot={lot} />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center">Pas de lots</div>
        )}
      </div>
    </div>
  );
};

export default ListeLots;
